/*
 *
 * Copyright (c) Vaisala Oyj. All rights reserved.
 *
 */

//@ts-nocheck

import * as React from 'react';
import { NotificationType } from '@vaisala/rockhopper-components';
import { Project, ProjectDetails, ProjectShort } from './@types';
import { UploadInfo, ProcessStatus, NCData, NCInfo } from './@types';
import { MonitoringData, StatusData, EnvData } from './@types';
import { ReconstructionInfo, ReconstructionSettings } from './@types';
import { AppNotification, useNotification } from '../notifications';
import { useAuth } from '../auth/AuthContext';


import { useLocation } from 'react-router-dom';

type ProjectState = {
  // Project Management
  launched: boolean;
  setLaunched: (launched : boolean) => void;
  projectList: ProjectShort[];
  fetchProjectList: () => void;
  processCount: number;
  project: Project;
  setProject: (project: Project) => void;
  fetchProject: (name : string) => void;

  // Data Management
  ncDataHistory: NCData;
  newNCData: NCData;
  newNCDataUntracked: NCData;
  loadHistoryStatus: ProcessStatus;
  setLoadHistoryStatus: (finderStatus: ProcessStatus) => void;
  finderStatus: ProcessStatus;
  setFinderStatus: (finderStatus: ProcessStatus) => void;
  uploadInfo: UploadInfo;
  setUploadInfo: (uploadInfo: UploadInfo) => void;

  // Monitoring
  monitoringDatetimes : [string, string];
  setMonitoringDatetimes : (monitoringDatetimes: [string, string]) => void;
  statusData: {[key : string] : StatusData[]|boolean};
  envData: {[key : string] : EnvData[]|boolean};
  monitoringData: {[key : string] : MonitoringData[]};
  monitoringLidarStatus: ProcessStatus;
  setMonitoringLidarStatus: (monitoringLidarStatus: ProcessStatus) => void;
  monitoringDataStatus: ProcessStatus;
  setMonitoringDataStatus: (monitoringDataStatus: ProcessStatus) => void;

  // Wind Reconstruction
  reconstructionSettings: ReconstructionSettings;
  fetchReconstructionSettings: () => void;
  updateReconstructionSettings: (new_rec: ReconstructionSettings) => void;
  reconstructedFiles : string[];
  errorFiles : string[];
  setSelectedVmms: (vmms: string[]) => void;
  setOnefile: (onefile: boolean) => void;
  fetchReconstruction: (mode : 'start'|'load'|'stop') => void;
  reconstructionInfo: ReconstructionInfo;
  setReconstructionInfo: (reconstructionInfo: ReconstructionInfo) => void;
};

export const initialProject: Project = {
  name: '',
  location: '',
  nc_folder: '',
  status: 'None',
  error: 'default'
};

export const initialReconstructionSettings: ReconstructionSettings = {
  frequency : '00:10:00',
  n_split : 1,
  max_desync : 2,
  ht_filter : 0,
  n_sigma : [0, 0],
  me_filter : 0
};

export const initialNCData: NCData = {
  ok : false,
  dates: [],
  status: [],
  data: {}
};

export const initialMonitoringData : MonitoringData = {
  id : "",
  Timestamp : 1,
  "Technical Avail L1" : 0,
  "Technical Avail L2" : 0,
  "Atm Avail L1" : 0,
  "Atm Avail L2" : 0,
  "Sync Time" : 0,
  "Sync Time STD" : 0,
  "Sync Time Max" : 0,
  "NOK %" : 0,
  "Uncorr L1" : 0,
  "Uncorr L2" : 0
}

export const initialStatusData : StatusData = {
  id : "",
  Timestamp : 1,
  "Acquisition board" : 0,
  "EDFA" : 0,
  "Scanner" : 0,
  "Time synchronization" : 0
}

export const initialEnvData : EnvData = {
  id : "",
  Timestamp : 1,
  "Humidity" : 0,
  "Disk occupation" : 0,
  "CPU load" : 0
}

export const ProjectContext = React.createContext<ProjectState>(
  {
    project: initialProject,
    reconstructionSettings: initialReconstructionSettings,
    launched: false,
    finderStatus : "NONE",
    loadHistoryStatus : "NONE",
    monitoringLidarStatus : "NONE",
    monitoringDataStatus : "NONE",
    ncDataHistory : initialNCData,
    newNCData : initialNCData,
    newNCDataUntracked : initialNCData,
    statusData: {},
    envData: {},
    monitoringData: {},
    monitoringDatetimes : ["", ""],
    uploadInfo: {status: "NONE", project : "", dates : [""], n_files : 0},
    reconstructionInfo: {status: "NONE", dates : [""]}
  } as ProjectState);

export const ProjectProvider = ({ children }: { children: React.ReactNode }) => {

  const [projectList, setProjectList] = React.useState<ProjectShort[]>([]);
  
  const updateProjectList = React.useRef<ReturnType<typeof setInterval>>();
  const updateUploadInfo = React.useRef<ReturnType<typeof setInterval>>();

  const [processCount, setProcessCount] = React.useState<number>(0);

  const [launched, setLaunched] = React.useState(false);
  const [project, setProject] = React.useState(initialProject);

  const [finderStatus, setFinderStatus] = React.useState<ProcessStatus>("LOADING");
  const [loadHistoryStatus, setLoadHistoryStatus] = React.useState<ProcessStatus>("LOADING");
  const [ncDataHistory, setNCDataHistory] = React.useState<NCData>(initialNCData);
  const [newNCData, setNewNCData] = React.useState<NCData>(initialNCData);
  const [newNCDataUntracked, setNewNCDataUntracked] = React.useState<NCData>(initialNCData);

  const [uploadInfo, setUploadInfo] = React.useState<UploadInfo>({status: "NONE", project : "", dates : [""], n_files : 0, n_files_uploaded : 0});

  const monitoringLidarProgress = React.useRef<ReturnType<typeof setInterval>>();
  const monitoringDataProgress = React.useRef<ReturnType<typeof setInterval>>();

  const [monitoringDatetimes, setMonitoringDatetimes] = React.useState<[string, string]>(["", ""]);

  const [monitoringData, setMonitoringData] = React.useState<{[key : string] : MonitoringData[]}>(() => {
    let initialData : {[key : string] : MonitoringData[]} = {}
    for (var vcup_id of project.details?.cup_ids??[]){
      initialData[vcup_id] = [initialMonitoringData];
    }

    return initialData;
  });
  
  const [statusData, setStatusData ] = React.useState<{[key : string] : StatusData[]|boolean}>(() => {
    let initialData : {[key : string] : StatusData[]|boolean} = {}
    initialData["ok"] = false;
    for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
      initialData[lidar??""] = [initialStatusData];
    }

    return initialData;
  });

  const [envData, setEnvData ] = React.useState<{[key : string] : EnvData[]|boolean}>(() => {
    let initialData : {[key : string] : EnvData[]|boolean} = {}
    initialData["ok"] = false;
    for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
      initialData[lidar??""] = [initialEnvData];
    }

    return initialData;
  });

  const [monitoringLidarStatus, setMonitoringLidarStatus] = React.useState<ProcessStatus>("LOADING");
  const [monitoringDataStatus, setMonitoringDataStatus] = React.useState<ProcessStatus>("LOADING");
  
  const [reconstructionInfo, setReconstructionInfo] = React.useState<ReconstructionInfo>({status: "NONE", dates : [""], files_reconstructed : 0});
  const [reconstructionSettings, setReconstructionSettings] = React.useState(initialReconstructionSettings);
  
  const [selectedVmms, setSelectedVmms] = React.useState<string[]>(project.details?.vmms??[]);
  const [onefile, setOnefile] = React.useState(true);
  
  const [reconstructedFiles, setReconstructedFiles] = React.useState<string[]>([]);
  const [errorFiles, setErrorFiles] = React.useState<string[]>([]);

  const { notifyById, notify } = useNotification();

  const fetchProjectList = async () => {
    const response = await secureFetch(`/api/projects/`);
    const data = await response.json();
    setProjectList(data);
  }

  const countProcess = () => {

    let countProcess : number = 0;

    if (uploadInfo.status === "RUNNING" || uploadInfo.status === "START"){
      countProcess += 2;
    }

    for (const project of projectList){
        if (project.wr_status === "reconstructing") countProcess++;
    }

    setProcessCount(countProcess);

  };
  const {status, user} = useAuth()
  const location = useLocation();


  React.useEffect(() => {
    if (status === 'ACTIVE' && user && location.pathname.includes('projects')) {
      fetchUploadInfo();
    }
    
    if (!launched) {
      countProcess();
    }
  }, [projectList, location.pathname]);

  React.useEffect(() => {
    countProcess();
  }, [uploadInfo.status]);
  
  React.useEffect(() => {
      countProcess();
  }, [reconstructionInfo.status]);


  React.useEffect(() => {
    if (launched){
      clearInterval(updateProjectList.current);
      clearInterval(updateUploadInfo.current);
      if (uploadInfo.status !== "RUNNING" || uploadInfo.project !== project.name) fetchDataHistory();
      fetchFinderStatus();
      fetchMonitoringLidarStatus();
      fetchMonitoringDataStatus();
      fetchReconstructionInfo()
      fetchReconstructionSettings();      
    } else if(status === 'ACTIVE' && user && location.pathname.includes('projects')) {
      resetProject();
      clearInterval(updateProjectList.current);
      clearInterval(updateUploadInfo.current);
      updateProjectList.current = setInterval(fetchProjectList, 2000);
      updateUploadInfo.current = setInterval(fetchUploadInfo, 2000); 
    }
  
    return (() => {
      clearInterval(updateProjectList.current);
      clearInterval(updateUploadInfo.current);
    })
  }, [launched, location.pathname]
);

  React.useEffect(() => {
    if (uploadInfo.status === 'RUNNING'){
      notify({
        type: NotificationType.Info,
        content: "Upload running for : " + uploadInfo.project,
        duration: 3000});
      setNewNCData(initialNCData);
    }

    else if (uploadInfo.status === 'FINISHED'){
      if (project.name === uploadInfo.project){
        setNewNCData(initialNCData);
        setNewNCDataUntracked(initialNCData);
        fetchDataHistory();
      }
      notify({
        type: NotificationType.Ok,
        content: "Upload finished for : " + uploadInfo.project,
        duration: 3000});
    }
    else if (uploadInfo.status === 'STOP'){
      if (project.name === uploadInfo.project){
        notify({
          type: NotificationType.Info,
          content: "Interrupting upload process... This can take a few seconds...",
          duration: 2000});
        setNewNCData(initialNCData);
        setNewNCDataUntracked(initialNCData);
        stopUploadProcess();
      }
    }
    else if (uploadInfo.status === 'ERROR'){
      notify({
        type: NotificationType.Alarm,
        content: "Error during upload for : " + uploadInfo.project,
        duration: 3000});
      if (!ncDataHistory.ok) fetchDataHistory();
    }

    }, [uploadInfo.status]);

  React.useEffect(() => {

    if (finderStatus === 'FINISHED'){
      fetchNewNCData('load');
    } else if (finderStatus === 'START'){
      fetchNewNCData('start');
    } else if (finderStatus === 'STOP'){
      notify({
        type: NotificationType.Info,
        content: "Interrupting finder process...",
        duration: 2000});
      fetchNewNCData('stop');
    }
  }, [finderStatus]);

  React.useEffect(() => {
    if (monitoringLidarStatus === 'RUNNING'){
      monitoringLidarProgress.current = setInterval(fetchMonitoringLidarStatus, 1500);
    }
    else if (monitoringLidarStatus === 'FINISHED'){
      clearInterval(monitoringLidarProgress.current);
      fetchStatusData('load');
    } else if (monitoringLidarStatus === 'ERROR'){
      clearInterval(monitoringLidarProgress.current);
    } else if (monitoringLidarStatus === 'START'){
      fetchStatusData('start');
    } else if (monitoringLidarStatus === 'STOP'){
      clearInterval(monitoringLidarProgress.current);
      notify({
        type: NotificationType.Info,
        content: "Interrupting lidar monitoring process...",
        duration: 2000});
      fetchStatusData('stop');
    } 

    return () => {
      clearInterval(monitoringLidarProgress.current);
    }

  }, [monitoringLidarStatus]);

  React.useEffect(() => {
    if (monitoringDataStatus === 'RUNNING'){
      monitoringDataProgress.current = setInterval(fetchMonitoringDataStatus, 1500);
    } else if (monitoringDataStatus === 'FINISHED'){
      clearInterval(monitoringDataProgress.current);
      fetchMonitoringData('load');
      if (monitoringLidarStatus === "NONE" && (!statusData["ok"] || !envData["ok"]))
        fetchStatusData('load');
    } else if (monitoringDataStatus === 'ERROR'){
      clearInterval(monitoringDataProgress.current);
    } else if (monitoringDataStatus === 'START'){
      fetchMonitoringData('start');
    } else if (monitoringDataStatus === 'STOP'){
      clearInterval(monitoringDataProgress.current);
      notify({
        type: NotificationType.Info,
        content: "Interrupting data monitoring process...",
        duration: 1500});
      fetchMonitoringData('stop');
    } 

    return () => {
      clearInterval(monitoringDataProgress.current);
    }

  }, [monitoringDataStatus]);
  
  React.useEffect(() => {

    if (reconstructionInfo.status === 'FINISHED'){
      fetchReconstruction('load');
      notifyById(AppNotification.ReconstructionFinished);
    } else if (reconstructionInfo.status === 'START'){
      setReconstructedFiles([]);
      setErrorFiles([]);
      fetchReconstruction('start');
    }
    else if (reconstructionInfo.status === 'RUNNING'){
      notifyById(AppNotification.ReconstructionRunning);
    }
    else if (reconstructionInfo.status === 'ERROR'){
      notifyById(AppNotification.ReconstructionError);
    } else if (reconstructionInfo.status === 'STOP'){
      notify({
        type: NotificationType.Info,
        content: "Interrupting reconstruction process...",
        duration: 2000});
      fetchReconstruction('stop');
    }

  }, [reconstructionInfo.status]);
  

  const fetchFinderStatus = async () => {

    if (!launched || !project.name) {
      return;
    }

    try {
      const response = await secureFetch(`/api/projects/${project.name}/finder_status`);
      const data = await response.json();

      if (response.ok){
        if (data.status === "wait") {
          await new Promise(resolve => setTimeout(resolve, 1500));
          fetchFinderStatus();
        } else if (data.status === "scanning" || data.status === "filtering" || data.status === "parsing" || data.status === "checking"){
          setFinderStatus("RUNNING");
        } else if (data.status === "finished"){
          setFinderStatus("FINISHED");
        } else if (data.status === "crashed"){
          setFinderStatus("ERROR");
        } else {
          setFinderStatus("NONE");
        }
      } else {
        notify({
          type: NotificationType.Error,
          content: "Error fetching finder status!",
          duration: 3000});
      }

    } catch (error) {
      notify({
        type: NotificationType.Error,
        content: "Error fetching finder status!",
        duration: 3000});
      console.error('Error fetching finder status :', error);
    }
  }
  const {secureFetch} = useAuth()

  const fetchUploadInfo = async () => {

      try {
        const response = await secureFetch(
          `/api/upload/status`
        );

        const data = await response.json();

        console.log("Checking Upload status");
  
        if (response.ok){
          if (data.status === "uploading"|| data.status === "stop_cmd"){
            setUploadInfo({status : "RUNNING", project : data.project, dates : data.dates, n_files : data.n_files, n_files_uploaded : data.total_counter});
          } else if (data.status === "finished"){
            setUploadInfo({status : "FINISHED", project : data.project, dates : data.dates, n_files : data.n_files, n_files_uploaded : data.total_counter});
          } else if (data.status === "crashed"){
            setUploadInfo({status : "ERROR", project : data.project, dates : data.dates, n_files : data.n_files, n_files_uploaded : 0});
          } else {
            setUploadInfo({status : "NONE", project : data.project, dates : data.dates, n_files : data.n_files, n_files_uploaded : 0});
          }
        } else {
          notify({
            type: NotificationType.Error,
            content: "Error fetching upload status!",
            duration: 3000});
        }
  
      } catch (error) {
        notify({
          type: NotificationType.Error,
          content: "Error fetching upload status!",
          duration: 3000});
        console.error('Error fetching upload status :', error);
      }
    };
  
    const stopUploadProcess = async () => {
      try {

        const response = await secureFetch(`/api/projects/` + project.name + '/new_data/upload' , {
          method: 'DELETE',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        const data = await response.json();

        if (!response.ok){
          const msg = data.message;
          console.error(msg);
          notify({
            type: NotificationType.Alarm,
            content: data.message ? data.message : "Error while stopping upload process",
            duration: 3000});
          setUploadInfo({...uploadInfo, status : 'RUNNING', n_files_uploaded : 0});
        } else {
          notify({
            type: NotificationType.Info,
            content: "Upload process interrupted !",
            duration: 3000});
          setUploadInfo({...uploadInfo, status : 'NONE', n_files_uploaded : 0});
          fetchDataHistory();
        }
      } catch (error) {
        notify({
          type: NotificationType.Alarm,
          content: error ? String(error) : "Error while uploading nc files",
          duration: 3000});
        setUploadInfo({...uploadInfo, status : 'RUNNING', n_files_uploaded : 0});
        console.error(error);
      }
    };

    const fetchMonitoringLidarStatus = async () => {

      if (!launched || !project.name) {
        return;
      }

      console.log("Checking Lidar Monitoring status");

      try {
        const response = await secureFetch(`/api/projects/${project.name}/monitoring_status/lidar`);
        const data = await response.json();
  
        if (monitoringLidarStatus !== "STOP"){
          if (response.ok){
            if (data.status === "wait") {
              if (monitoringLidarStatus !== "RUNNING") fetchMonitoringLidarStatus();
            } else if (data.status === "running"){
              if (monitoringLidarStatus !== "RUNNING") setMonitoringLidarStatus("RUNNING");
            } else if (data.status === "finished"){
              setMonitoringLidarStatus("FINISHED");
            } else if (data.status === "crashed"){
              setMonitoringLidarStatus("ERROR");
            } else {
              if (monitoringLidarStatus !== "RUNNING") setMonitoringLidarStatus("NONE");
            }
          } else {
            notify({
              type: NotificationType.Error,
              content: "Error fetching monitoring lidar status!",
              duration: 3000});
          }          
        }

  
      } catch (error) {
        notify({
          type: NotificationType.Error,
          content: "Error fetching monitoring lidar status!",
          duration: 3000});
        console.error('Error fetching monitoring lidarstatus :', error);
      }
    }

    const fetchMonitoringDataStatus = async () => {

      if (!launched || !project.name) {
        return;
      }
      console.log("Checking Data Monitoring status");

      try {
        const response = await secureFetch(`/api/projects/${project.name}/monitoring_status/data`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
  
        if (monitoringDataStatus !== "STOP"){
          if (response.ok){
            if (data.status === "wait") {
              if (monitoringDataStatus !== "RUNNING") fetchMonitoringDataStatus();
            } else if (data.status === "running"){
              if (monitoringDataStatus !== "RUNNING") setMonitoringDataStatus("RUNNING");
            } else if (data.status === "finished"){
              setMonitoringDataStatus("FINISHED");
            } else if (data.status === "crashed"){
              setMonitoringDataStatus("ERROR");
            } else {
              if (monitoringDataStatus !== "RUNNING") setMonitoringDataStatus("NONE");
            }
          } else {
            notify({
              type: NotificationType.Error,
              content: "Error fetching finder status!",
              duration: 3000});
          }          
        }

  
      } catch (error) {
        notify({
          type: NotificationType.Error,
          content: "Error fetching finder status!",
          duration: 3000});
        console.error('Error fetching finder status :', error);
      }
    }

  const fetchReconstructionInfo = async () => {
    
      if (!launched || !project.name) {
        return;
      }

      try {
        const response = await secureFetch(`/api/projects/` + project.name + '/reconstruction_status'); 
        const data = await response.json();

        console.log("Checking Reconstruction status");

        if (response.ok){
          if (data.status === "wait")
            fetchReconstructionInfo();
          else if (data.status === "reconstructing"){
            setReconstructionInfo({...reconstructionInfo, status : "RUNNING", dates : data.dates});
          } else if (data.status === "finished"){
            setReconstructionInfo({...reconstructionInfo, status : "FINISHED", dates : data.dates, files_reconstructed : data.total_counter});
          } else if (data.status === "crashed"){
            setReconstructionInfo({status : "ERROR", dates : data.dates, files_reconstructed : 0});
          } else {
            setReconstructionInfo({...reconstructionInfo, status : "NONE"});
          }
        } else {
          notify({
            type: NotificationType.Error,
            content: "Error fetching reconstruction status!",
            duration: 3000});
        }
  
      } catch (error) {
        notify({
          type: NotificationType.Error,
          content: "Error fetching reconstruction status!",
          duration: 3000});
        console.error('Error fetching reconstruction status :', error);
      }
    };

  const fetchProject = async ( name : string ) => {
    try {
      setProject(initialProject);
      const response = await secureFetch(`/api/projects/` + name); 
      const data = await response.json();

      const ok = await response.ok;

      if(!ok){
        
        const msg = data.message;
        // Display an error notification for missing required fields
        notify({
          type: NotificationType.Error,
          content: msg?msg:"Error during API request",
          duration: 3000});
      }
      else {      
      
        const new_project_details: ProjectDetails = data;

        const new_project: Project = {
          name: data.name,
          location: data.location,
          nc_folder: data.nc_folder,
          status: data.status,
          details: new_project_details,
          error: "201"
        }
        setProject(new_project);
      }
    } catch (error) {
      notify({
        type: NotificationType.Error,
        content: error?String(error):"Error during API request",
        duration: 3000});
      console.error('Error fetching project:', error);
    }
  };

  const fetchReconstructionSettings = async () => {

    if (!launched || !project.name) {
      return;
    }

    try {

      const response = await secureFetch(`/api/projects/` + project.name + '/data/reconstruction/settings');
      const data = await response.json();

      if (response.ok) {
        setReconstructionSettings(data);
      } else {
        notify({
          type: NotificationType.Warning,
          content: "Warning reconstruction settings could not be fetched !",
          duration: 1000});
      }

    } catch (error){
      notify({
        type: NotificationType.Error,
        content: error?String(error):"Error during API request",
        duration: 3000});
      console.error('Error fetching reconstruction settings:', error);
    }
  };

  const updateReconstructionSettings = async (new_settings : ReconstructionSettings) => {
    try {
      const response = await secureFetch(`/api/projects/` + project.name + '/data/reconstruction/settings', {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(new_settings),
      });

      if (response.ok){
        notify({
          type: NotificationType.Ok,
          content: "Reconstruction settings successfully updated",
          duration: 3000});
        fetchReconstructionSettings();
      }
      else {
        notify({
          type: NotificationType.Warning,
          content: "Reconstruction settings could not be updated",
          duration: 3000});
      }
    } catch (error) {
      notify({
        type: NotificationType.Error,
        content: error?String(error):"Error during API request",
        duration: 3000});
      console.error('Error fetching reconstruction settings:', error);
    }

  };

  const fetchDataHistory = async () => {
    
    if (!launched || !project.name) {
      return;
    }

    setLoadHistoryStatus("RUNNING");

    try {
      const response = await secureFetch(`/api/projects/` + project.name + '/data/all', {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      if (response.ok){
        if (data.dates.length > 0){


          const history_dates : string[] = data.dates;
          const history_status : boolean[] = data.status;
          const history_infos : {[key : string] : NCInfo[]} = data.history_info;
          
          const ncDataHistoryTemp : NCData = {
            ok : true,
            dates : history_dates,
            status : history_status,
            data : history_infos
          };

          setMonitoringDatetimes([history_dates[0] + "T00:00:00", history_dates[history_dates.length-1] + "T23:59:59"]);

          setNCDataHistory(ncDataHistoryTemp);
          setLoadHistoryStatus("NONE");
        }
        else{
          setLoadHistoryStatus("NONE");
          notify({
            type: NotificationType.Warning,
            content: "No data loaded in this project",
            duration: 1500});
          setNCDataHistory(initialNCData);
        }        
      } else {
        console.error(data.message);
        setNCDataHistory(initialNCData);
        setLoadHistoryStatus("ERROR");
        notify({
          type: NotificationType.Alarm,
          content: data.message ? "Can't load data history" + data.message : "Can't load data history",
          duration: 3000});
      }
    } catch (error) {
      console.error(error);
      setNCDataHistory(initialNCData);
      setLoadHistoryStatus("ERROR");
      notify({
        type: NotificationType.Alarm,
        content: "Can't load data history : " + String(error),
        duration: 3000});
    }
  }

  const fetchNewNCData = async (mode : "start"|"load"|"stop") => {
    
    if (!launched || !project.name) {
      return;
    }

    if (mode === "start"){
      setFinderStatus("RUNNING");
      try {
        const response = await secureFetch(`/api/projects/` + project.name + '/new_data', {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          }
        });
  
        const data = await response.json();
  
        if (response.ok && data.finder){
          console.log("Finder process launched!");
        } else {
          console.error(data.message);
          setNewNCData(initialNCData);   
          setNewNCDataUntracked(initialNCData);
          notify({
            type: NotificationType.Alarm,
            content: data.message ? data.message : "Error while looking for new files",
            duration: 5000});
        }
      } catch (error) {
        console.error(error);
        setNewNCData(initialNCData);
        setNewNCDataUntracked(initialNCData);
        notify({
          type: NotificationType.Alarm,
          content: "Error while looking for new files : " + String(error),
          duration: 3000});
      }
    }
    else if (mode === "load") {
      setFinderStatus("LOADING");
      try {
        const response = await secureFetch(`/api/projects/` + project.name + '/new_data', {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          }
        });
  
        const data = await response.json();
  
        if (response.ok){
          
          const total_new : number = data.total
  
          if (data.new_dates.length === 0 || total_new === 0){
            setNewNCData(initialNCData);     
            setNewNCDataUntracked(initialNCData);
            setFinderStatus("NONE");
            notify({
              type: NotificationType.Warning,
              content: "No new NetCDF Files found",
              duration: 3000});
          }
          else{
    
            const fetched_new_dates : string[] = data.new_dates;
            const fetched_n_files_day : number[] = data.n_files_day;
            const fetched_new_status : boolean[] = data.new_status;
            const fetched_new_infos : {[key : string] : NCInfo[]} = data.new_nc_info;
            const fetched_untracked_infos : {[key : string] : NCInfo[]} = data.untracked_nc_info;
  
            const newNCDataTemp : NCData = {
              ok : true,
              dates : fetched_new_dates,
              status : fetched_new_status,
              data : fetched_new_infos,
              nfiles_per_day : fetched_n_files_day
            };
            
            const newNCDataUntrackedTemp : NCData = {
              ok : true,
              dates : fetched_new_dates,
              status : fetched_new_status,
              data : fetched_untracked_infos
            };
  
            setNewNCData(newNCDataTemp);
            setNewNCDataUntracked(newNCDataUntrackedTemp);
            setFinderStatus("NONE");
            setUploadInfo({status : 'READY', project: project.name, dates : fetched_new_dates, n_files : total_new, n_files_uploaded : 0});
            notify({
              type: NotificationType.Ok,
              content: "New files found",
              duration: 3000});
          }
        } else {
          console.error(data.message);
          setNewNCData(initialNCData);   
          setNewNCDataUntracked(initialNCData);     
          setFinderStatus('NOT_FOUND');
          notify({
            type: NotificationType.Alarm,
            content: data.message ? data.message : "Error while looking for new files",
            duration: 5000});
        }
      } catch (error) {
        console.error(error);
        setNewNCData(initialNCData);
        setNewNCDataUntracked(initialNCData);
        setFinderStatus("ERROR");
        notify({
          type: NotificationType.Alarm,
          content: "Error while looking for new files : " + String(error),
          duration: 3000});
      }
    }
    else if (mode === "stop") {
      try {
        const response = await secureFetch(`/api/projects/` + project.name + '/new_data', {
          method: 'DELETE',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          }
        });
  
        const data = await response.json();
  
        if (response.ok){
          setNewNCData(initialNCData);   
          setNewNCDataUntracked(initialNCData);
          notify({
            type: NotificationType.Info,
            content: "Finder process interrupted !",
            duration: 1500});
          setFinderStatus("NONE");
        } else {
          console.error(data.message);
          notify({
            type: NotificationType.Alarm,
            content: data.message ? data.message : "Can't stop finder process",
            duration: 5000});
          setFinderStatus("RUNNING");
        }
      } catch (error) {
        console.error(error);
        notify({
          type: NotificationType.Alarm,
          content: "Can't stop finder process",
          duration: 3000});
        setFinderStatus("RUNNING");
      }
    }
  }

  const fetchStatusData = async (mode : "start"|"load"|"stop") => {
    
    if (!launched || !project.name) {
      return;
    }

    if (mode === "start") {

      setMonitoringLidarStatus("RUNNING");
      
      try {

        const response = await secureFetch(`/api/projects/` + project.name + '/monitoring/status/' + monitoringDatetimes[0] + "/" + monitoringDatetimes[1], {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          }
        });
  
        const data = await response.json();
  
        if (response.ok && data.monitoring_status){
  
          if (data.errors){
            const files_errors = data.errors
            notify({
              type: NotificationType.Warning,
              content: "Errors while parsing " + files_errors + " : Wrong data format (please use Windforge standard Status files)",
              duration: 5000});
          }
  
          console.log("Monitoring lidar process successful");
        } else {
          const msg : string  = data.message;
  
          let resetEnvData : {[key : string] : EnvData[]|boolean} = {}
          for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
            resetEnvData[lidar??""] = [initialEnvData];
          }        
          
          let resetStatusData : {[key : string] : StatusData[]|boolean} = {}
          for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
            resetStatusData[lidar??""] = [initialStatusData];
          }
  
          resetStatusData["ok"] = false;
          resetEnvData["ok"] = false;
  
          setEnvData(resetEnvData);
          setStatusData(resetStatusData);
  
          notify({
            type: NotificationType.Alarm,
            content: msg?msg:"Can't fetch lidars status",
            duration: 3000});
        }
  
      } catch (error) {
        console.log(error);
  
        let resetEnvData : {[key : string] : EnvData[]|boolean} = {}
        for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
          resetEnvData[lidar??""] = [initialEnvData];
        }
        
        let resetStatusData : {[key : string] : StatusData[]|boolean} = {}
        for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
          resetStatusData[lidar??""] = [initialStatusData];
        }
        
        resetStatusData["ok"] = false;
        resetEnvData["ok"] = false;
  
        setEnvData(resetEnvData);
        setStatusData(resetStatusData);
        
        notify({
          type: NotificationType.Alarm,
          content: "Can't fetch lidars status " + String(error),
          duration: 3000});
      }
    } else if (mode === 'load') {
      
      setMonitoringLidarStatus("LOADING");
      
      try {

        const response = await secureFetch(`/api/projects/` + project.name + '/monitoring/status');
  
        const data = await response.json();
  
        if (response.ok) {
  
          const dates = data.dates;
  
          const status_data : {[key : string] : EnvData[]|boolean} = data.data.sys_status_data;
          const env_data : {[key : string] : EnvData[]|boolean} = data.data.env_status_data;

          for (const lidar of [project.details?.lidar1, project.details?.lidar2]){
            if ((lidar??"") in data.data && data.data[lidar??""] === 0){
              notify({
                type: NotificationType.Warning,
                content: "No status data for " + lidar,
                duration: 1000});
            }
          }
          
          status_data["ok"] = true;
          env_data["ok"] = true;
  
          setMonitoringDatetimes(dates);
  
          setEnvData(env_data);
          setStatusData(status_data);
  
          setMonitoringLidarStatus("NONE");
  
        } else {
          const msg : string  = data.message;
          setMonitoringLidarStatus("ERROR");
  
          let resetEnvData : {[key : string] : EnvData[]|boolean} = {}
          for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
            resetEnvData[lidar??""] = [initialEnvData];
          }        
          
          let resetStatusData : {[key : string] : StatusData[]|boolean} = {}
          for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
            resetStatusData[lidar??""] = [initialStatusData];
          }
  
          resetStatusData["ok"] = false;
          resetEnvData["ok"] = false;
  
          setEnvData(resetEnvData);
          setStatusData(resetStatusData);
  
          notify({
            type: NotificationType.Alarm,
            content: msg?msg:"Can't fetch lidars status",
            duration: 3000});
        }
  
      } catch (error) {
        console.log(error);
  
        setMonitoringLidarStatus("ERROR");
  
        let resetEnvData : {[key : string] : EnvData[]|boolean} = {}
        for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
          resetEnvData[lidar??""] = [initialEnvData];
        }
        
        let resetStatusData : {[key : string] : StatusData[]|boolean} = {}
        for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
          resetStatusData[lidar??""] = [initialStatusData];
        }
        
        resetStatusData["ok"] = false;
        resetEnvData["ok"] = false;
  
        setEnvData(resetEnvData);
        setStatusData(resetStatusData);
        
        notify({
          type: NotificationType.Alarm,
          content: "Can't fetch lidars status " + String(error),
          duration: 3000});
      }
    } else if (mode === 'stop') {
      try {

        const response = await secureFetch(`/api/projects/` + project.name + '/monitoring/status', {
          method: 'DELETE',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          }
        });
  
        const data = await response.json();
  
        if (response.ok) {
            
          let resetEnvData : {[key : string] : EnvData[]|boolean} = {}
          for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
            resetEnvData[lidar??""] = [initialEnvData];
          }        
          
          let resetStatusData : {[key : string] : StatusData[]|boolean} = {}
          for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
            resetStatusData[lidar??""] = [initialStatusData];
          }
  
          resetStatusData["ok"] = false;
          resetEnvData["ok"] = false;
  
          setEnvData(resetEnvData);
          setStatusData(resetStatusData);

          setMonitoringLidarStatus("NONE");

          notify({
            type: NotificationType.Info,
            content: "Lidar monitoring process interrupted",
            duration: 3000});
        } else {
          const msg : string  = data.message;
  
          let resetEnvData : {[key : string] : EnvData[]|boolean} = {}
          for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
            resetEnvData[lidar??""] = [initialEnvData];
          }        
          
          let resetStatusData : {[key : string] : StatusData[]|boolean} = {}
          for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
            resetStatusData[lidar??""] = [initialStatusData];
          }
  
          resetStatusData["ok"] = false;
          resetEnvData["ok"] = false;
  
          setEnvData(resetEnvData);
          setStatusData(resetStatusData);

          setMonitoringLidarStatus("RUNNING");
  
          notify({
            type: NotificationType.Alarm,
            content: msg?msg:"Can't stop lidar monitoring process",
            duration: 3000});
        }
  
      } catch (error) {
        console.log(error);
  
        let resetEnvData : {[key : string] : EnvData[]|boolean} = {}
        for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
          resetEnvData[lidar??""] = [initialEnvData];
        }
        
        let resetStatusData : {[key : string] : StatusData[]|boolean} = {}
        for (const lidar of [project.details?.lidar1, project.details?.lidar2]??[]){
          resetStatusData[lidar??""] = [initialStatusData];
        }
        
        resetStatusData["ok"] = false;
        resetEnvData["ok"] = false;
  
        setEnvData(resetEnvData);
        setStatusData(resetStatusData);
  
        setMonitoringLidarStatus("RUNNING");
  
        notify({
          type: NotificationType.Alarm,
          content: String(error) + " : Can't stop lidar monitoring process",
          duration: 3000});
      }
    }
  }

  const fetchMonitoringData = async (mode : "start"|"load"|"stop") => {

    if (!launched || !project.name) {
      return;
    }

    if (mode === "start") {

      setMonitoringDataStatus("RUNNING");

      try {

        const response = await secureFetch(`/api/projects/` + project.name + '/monitoring/data/' + monitoringDatetimes[0] + "/" + monitoringDatetimes[1], {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          }
        });
  
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
  
        if (response.ok && data.monitoring_data){
          console.log("Monitoring data process successful");
        } else {
          const msg : string = data.message;
  
          let newMonitoringData : {[key : string] : MonitoringData[]} = {};
          for (const vcup_id of project.details?.cup_ids??[]) {
            newMonitoringData[vcup_id] = [initialMonitoringData];
          }
          setMonitoringData(newMonitoringData);
  
          notify({
            type: NotificationType.Alarm,
            content: msg?msg:"Can't fetch monitoring data",
            duration: 3000});
        }
  
  
      } catch (error) {
        console.log(error);
  
        let newMonitoringData : {[key : string] : MonitoringData[]} = {};
        for (const vcup_id of project.details?.cup_ids??[]) {
          newMonitoringData[vcup_id] = [initialMonitoringData];
        }
        setMonitoringData(newMonitoringData);
  
        setMonitoringDataStatus("ERROR");
              
        notify({
          type: NotificationType.Alarm,
          content: "Can't fetch monitoring data " + String(error),
          duration: 2000});
      }
    } else if (mode === 'load') {
      
      setMonitoringDataStatus("LOADING");
    
      try {
        const response = await secureFetch(`/api/projects/` + project.name + '/monitoring/data');
  
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
  
        if (response.ok) {
  
          setMonitoringDatetimes(data.dates);
  
          let newMonitoringData : {[key : string] : MonitoringData[]} = {};
          for (const vcup_id of project.details?.cup_ids??[]) {
            if (vcup_id in data) {
              newMonitoringData[vcup_id] = data[vcup_id];
            } else {
              notify({
                type: NotificationType.Warning,
                content: "No monitoring data for " + vcup_id,
                duration: 3000});
              newMonitoringData[vcup_id] = [initialMonitoringData];
            }
          }

          setMonitoringData(newMonitoringData);
          notify({
            type: NotificationType.Ok,
            content: "Monitoring data ready !",
            duration: 3000});
  
          setMonitoringDataStatus("NONE");
        } else {
          const msg : string = data.message;
  
          let newMonitoringData : {[key : string] : MonitoringData[]} = {};
          for (const vcup_id of project.details?.cup_ids??[]) {
            newMonitoringData[vcup_id] = [initialMonitoringData];
          }
          setMonitoringData(newMonitoringData);
  
          setMonitoringDataStatus("ERROR");
  
          notify({
            type: NotificationType.Alarm,
            content: msg?msg:"Can't fetch monitoring data",
            duration: 3000});
        }
  
  
      } catch (error) {
        console.log(error);
  
        let newMonitoringData : {[key : string] : MonitoringData[]} = {};
        for (const vcup_id of project.details?.cup_ids??[]) {
          newMonitoringData[vcup_id] = [initialMonitoringData];
        }
        setMonitoringData(newMonitoringData);
  
        setMonitoringDataStatus("ERROR");
              
        notify({
          type: NotificationType.Alarm,
          content: "Can't fetch monitoring data " + String(error),
          duration: 3000});
      }      
    } else if (mode === 'stop') {
      try {

        const response = await secureFetch(`/api/projects/` + project.name + '/monitoring/data', {
          method: 'DELETE',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
  
        const data = await response.json();
  
        if (response.ok) {
            
          let newMonitoringData : {[key : string] : MonitoringData[]} = {};
          for (const vcup_id of project.details?.cup_ids??[]) {
            newMonitoringData[vcup_id] = [initialMonitoringData];
          }
          setMonitoringData(newMonitoringData);

          setMonitoringDataStatus("NONE");

          notify({
            type: NotificationType.Info,
            content: "Data monitoring process interrupted",
            duration: 1500});
        } else {
          const msg : string  = data.message;
  
          let newMonitoringData : {[key : string] : MonitoringData[]} = {};
          for (const vcup_id of project.details?.cup_ids??[]) {
            newMonitoringData[vcup_id] = [initialMonitoringData];
          }
          setMonitoringData(newMonitoringData);

          setMonitoringDataStatus("RUNNING");
  
          notify({
            type: NotificationType.Alarm,
            content: msg?msg:"Can't stop data monitoring process",
            duration: 3000});
        }
  
      } catch (error) {
        console.log(error);
  
        let newMonitoringData : {[key : string] : MonitoringData[]} = {};
        for (const vcup_id of project.details?.cup_ids??[]) {
          newMonitoringData[vcup_id] = [initialMonitoringData];
        }
        setMonitoringData(newMonitoringData);
  
        notify({
          type: NotificationType.Alarm,
          content: String(error) + " : Can't stop lidar monitoring process",
          duration: 2000});

        setMonitoringDataStatus("RUNNING");
      }
    }
  }

  const fetchReconstruction = async (mode : "start"|"load"|"stop") => {

    if (!launched || !project.name) {
      return;
    }

    if (mode === 'start'){

      setReconstructionInfo({...reconstructionInfo, status : "RUNNING"});

      try {
        const response = await secureFetch(`/api/projects/` + project.name + '/data/reconstruction', {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            'dates_list' : reconstructionInfo.dates,
            'selected_vmms' : selectedVmms,
            'onefile' : onefile,
          })
        });

        const data = await response.json();

        if (!response.ok){
          notify({
            type: NotificationType.Alarm,
            content: data.message ? data.message : "Error during reconstruction process",
            duration: 3000
          });
        } else {
          if (data.reconstruction){
            console.log('Reconstruction process finished succesfully !');
          }
        }
      } catch (error) {
        console.error(error);
      }      
    } else if (mode === "load") {

      setReconstructionInfo({...reconstructionInfo, status : "LOADING"});

      try {
        const response = await secureFetch(`/api/projects/` + project.name + '/data/reconstruction' , {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          }
        });
  
        const data = await response.json();
  
        if (!response.ok){
          console.error("Can't fetch reconstructed files");
          notify({
            type: NotificationType.Alarm,
            content: data.message ? data.message : "Can't load reconstruction files",
            duration: 3000
          });
          setReconstructionInfo({...reconstructionInfo, status : 'ERROR'})
        }
  
        else {
          if (data.res_files.length === 0 && data.res_error_files.length === 0){
            setReconstructedFiles([]);
            setErrorFiles([]);
            notify({
              type: NotificationType.Alarm,
              content: "No files to recover!",
              duration: 3000
            });
          }
          else if (data.res_files.length === 0 && data.res_error_files.length > 0){
            setReconstructedFiles([]);
            setErrorFiles(data.res_error_files);
            notify({
              type: NotificationType.Alarm,
              content: "No cups have been successfully processed. See log files for details.",
              duration: 3000
            });
          }
          else if (data.res_files.length > 0 && data.res_error_files.length > 0){
            setReconstructedFiles(data.res_files);
            setErrorFiles(data.res_error_files);
            notify({
              type: NotificationType.Warning,
              content: "Some cups have not been successfully processed. See log files for details",
              duration: 5000
            });
          } else {
            setReconstructedFiles(data.res_files);
          }
          setReconstructionInfo({...reconstructionInfo, status : 'NONE'})
        }
  
      } catch (error) {
        console.error("Error during API fetch");
        console.error(error);
        setReconstructionInfo({...reconstructionInfo, status : 'ERROR'})
      }
    } else if (mode === "stop") {

      try {

        const response = await secureFetch(`/api/projects/` + project.name + '/data/reconstruction' , {
          method: 'DELETE',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            'dates_list' : reconstructionInfo.dates,
          })
        });
  
        const data = await response.json();
  
        if (!response.ok){
          notify({
            type: NotificationType.Alarm,
            content: data.message ? data.message : "Can't stop reconstruction process",
            duration: 3000
          });
          setReconstructionInfo({...reconstructionInfo, status : 'RUNNING'})
        }
  
        else {
          setReconstructedFiles([]);
          setErrorFiles([]);
          notify({
            type: NotificationType.Info,
            content: "Reconstruction process interrupted successfully",
            duration: 3000
          });
          setReconstructionInfo({...reconstructionInfo, status : 'NONE'})
        }
  
      } catch (error) {
        console.error(error);
        setReconstructedFiles([]);
        setErrorFiles([]);
        notify({
          type: NotificationType.Alarm,
          content: String(error) + " : Can't stop reconstruction process",
          duration: 3000
        });
        setReconstructionInfo({...reconstructionInfo, status : 'RUNNING'})
      }

    }

  }

  const resetProject = () => {
  
    setFinderStatus("LOADING");
    setLoadHistoryStatus("LOADING");

    setNCDataHistory(initialNCData);
    setNewNCData(initialNCData);
    setNewNCDataUntracked(initialNCData);

    setMonitoringDataStatus("LOADING");
    setMonitoringLidarStatus("LOADING");

    setMonitoringDatetimes(["", ""]);
    setMonitoringData({});
    setStatusData({"ok" : false});
    setEnvData({"ok" : false});

    clearInterval(monitoringLidarProgress.current);
    clearInterval(monitoringDataProgress.current);
    
    setReconstructionInfo({status: "NONE", dates : [""], files_reconstructed : 0});
    setReconstructionSettings(initialReconstructionSettings);
    setReconstructedFiles([]);
    setErrorFiles([]);
    setSelectedVmms([]);
    setOnefile(true);

    setProject(initialProject);
  }

  return <ProjectContext.Provider
          value={{
            projectList, fetchProjectList, processCount,
            project, fetchProject, setProject,
            reconstructionSettings, fetchReconstructionSettings, updateReconstructionSettings,
            launched, setLaunched,
            finderStatus, setFinderStatus,
            loadHistoryStatus, setLoadHistoryStatus,
            uploadInfo, setUploadInfo,
            monitoringLidarStatus, setMonitoringLidarStatus,
            monitoringDataStatus, setMonitoringDataStatus,
            reconstructionInfo, setReconstructionInfo,
            ncDataHistory, newNCData, newNCDataUntracked,
            statusData, envData, monitoringData,
            monitoringDatetimes, setMonitoringDatetimes,
            reconstructedFiles,
            errorFiles,
            fetchReconstruction,
            setSelectedVmms, setOnefile
          }}>
            {children}
          </ProjectContext.Provider>;
};

export { useProject } from './useProject';
